import React, { useState } from 'react'
import { Button, Divider, message, Modal, Table, Tag } from 'antd'
import { SessionRevisor } from '../../types/session'
import {} from '@ant-design/icons'
import './style.less'
import { advisorClientOverview, ClientOverview } from './types'
import { httpPostRevisor } from '../../services/http'
import { loadStripe } from '@stripe/stripe-js'
import { CustomerPortal } from '../../lib/interfaces'
import { ITaxYear } from '../ComplianceReports/types'

const api_key = process.env.GATSBY_STRIPE_PUBLIC_KEY || ''

export default function RevisorOverview({
    loadOverviewData,
    session,
    clients,
    setSelectedKey,
}: {
    loadOverviewData: (i?: string) => Promise<ITaxYear[]>
    session: SessionRevisor
    clients: ClientOverview[]
    setClients: (d: ClientOverview[]) => void
    setSelectedKey: (k: string) => void
}) {
    const [MBLoading, setMBLoading] = useState(false)

    const buyTaxYear = async (advisor_access_id: string) => {
        setMBLoading(true)
        try {
            const res = await httpPostRevisor<CustomerPortal & { already_accepted: boolean }>(
                `/auth-revisor/tax-year-customer-portal/${advisor_access_id}`
            )
            if (!res.data) return
            if (res.data.url && typeof window !== 'undefined') {
                window.location.href = res.data.url
            } else if (res.data.sessionId) {
                const sessId = res.data.sessionId
                const stripe = await loadStripe(api_key)
                const stripeRes = await stripe?.redirectToCheckout({
                    sessionId: sessId,
                })
                Modal.error({
                    title: 'Could not complete checkout',
                    content: stripeRes?.error.message,
                })
            }
        } catch (error) {
            message.error('Something went wrong: ' + error.toString(), 5)
        } finally {
            setMBLoading(false)
        }
    }

    const expandedRowRender = (record: ClientOverview, idx) => {
        const columns = [
            {
                dataIndex: 'is_accepted',
                key: 'is_accepted',
                // width: '10%',
                render: (t, record: advisorClientOverview) => {
                    return (
                        <>
                            {record.is_accepted ? (
                                <Button
                                    onClick={() => {
                                        setSelectedKey(`taxYear;;;${record.ll_tax_year_id}`)
                                    }}
                                >
                                    View
                                </Button>
                            ) : (
                                <Button
                                    loading={MBLoading}
                                    onClick={() => {
                                        buyTaxYear(record.id)
                                    }}
                                >
                                    Accept
                                </Button>
                            )}
                        </>
                    )
                },
            },
            {
                title: 'Tax Year',
                dataIndex: 'tax_year',
                key: 'tax_year',
                width: '5%',
            },
            {
                title: 'Status',
                dataIndex: 'is_locked',
                key: 'is_locked',
                width: '10%',
                render: (t, record: advisorClientOverview) => {
                    return (
                        <>
                            {record.is_locked ? <Tag color="success">Locked</Tag> : <Tag>Open</Tag>}
                        </>
                    )
                },
            },
            {
                title: 'Exemption periods',
                dataIndex: 'id',
                key: 'id',
                width: '10%',
                render: (t, record: advisorClientOverview) => {
                    return <>{record.reports_stats.reports_data.length}</>
                },
            },
            {
                title: 'Calendar days',
                dataIndex: 'id',
                key: 'id',
                width: '10%',
                render: (t, record: advisorClientOverview) => {
                    return <>{record.reports_stats.calendar_days}</>
                },
            },
            {
                title: 'Amount of documentation',
                dataIndex: 'id',
                key: 'id',
                width: '10%',
                render: (t, record: advisorClientOverview) => {
                    return <>{record.reports_stats.all_documentation}</>
                },
            },
            {
                title: 'Days with notes',
                dataIndex: 'id',
                key: 'id',
                width: '10%',
                render: (t, record: advisorClientOverview) => {
                    return <>{record.reports_stats.note_days}</>
                },
            },
            {
                title: 'Empty days',
                dataIndex: 'id',
                key: 'id',
                width: '10%',
                render: (t, record: advisorClientOverview) => {
                    const dayCount =
                        record.reports_stats.calendar_days - record.reports_stats.documentation_days
                    return (
                        <>
                            {dayCount > 0 ? (
                                <Tag color="error">{dayCount}</Tag>
                            ) : (
                                <Tag>{dayCount}</Tag>
                            )}
                        </>
                    )
                },
            },
            {
                title: 'Maximum DK-daycount',
                dataIndex: 'id',
                key: 'id',
                width: '10%',
                render: (t, record: advisorClientOverview) => {
                    return (
                        <>
                            {record.reports_stats.max_day_count > 42 ? (
                                <Tag color="error">{record.reports_stats.max_day_count}</Tag>
                            ) : (
                                <Tag color="success">{record.reports_stats.max_day_count}</Tag>
                            )}
                        </>
                    )
                },
            },
        ]
        return <Table columns={columns} dataSource={record.tax_years} pagination={false} />
    }

    const columns = [
        {
            title: 'Client',
            dataIndex: 'id',
            key: 'id',
            width: '70%',
            render: (t, record: ClientOverview) => {
                return (
                    <>
                        <p>{record.client_info.name}</p>
                        <p>{record.client_info.email}</p>
                    </>
                )
            },
        },
        {
            title: 'Tax Years available',
            dataIndex: 'id',
            key: 'id',
            width: '20%',
            render: (t, record: ClientOverview) => {
                const pendingTaxYears = record.tax_years.filter((ty) => !ty.is_accepted)
                return (
                    <>
                        <p>{record.tax_years.length}</p>
                        {pendingTaxYears.length > 0 && <p>{pendingTaxYears.length} is pending</p>}
                    </>
                )
            },
        },
    ]

    return (
        <div
            style={{
                width: '95%',
                margin: 'auto',
                marginTop: 20,
                marginBottom: 20,
            }}
        >
            <h1>Home</h1>
            <Divider />
            <Table
                columns={columns}
                dataSource={clients}
                expandable={{ expandedRowRender }}
                bordered
                pagination={false}
                size="small"
                className="report-table"
            />
        </div>
    )
}
